import { EHttpMethods } from "../../../../enums";
import { AUTH_BASE_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { LIST_ORGANIZATIONS_URL, ORGANISATIONS_ADMIN_URL } from "./constants";
import {
  uploadFile,
  uploadFileWithPresignedUrl,
} from "../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../s3Integration/enums";
import {
  IAddOrganizationDocumentationBody,
  IOrganizationAddressBankInfoBody,
  IOrganizationAddressBankInfoResponse,
  IOrganizationBankInfoResponse,
  IOrganizationDocumentationResponse,
  IOrganizationRegistryNumbersBody,
  IOrganizationSelfRegisterBody,
  IOrganizationTierResponse,
  IOrgClientCreatedResponse,
  IOrgClientListResponse,
} from "./models";
import { IAddNewUserBody, IClientAdminResponse } from "./types";

// eslint-disable-next-line no-unused-vars
const defaultProvide = async () => await getToken();

export const getAllOrganizations = (
  page = 0,
  size = 10,
  searchValue = ""
): Promise<any> => {
  const url = {
    get value() {
      if (searchValue === "") {
        return `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}?page=${page}&size=${size}&sort=createdAt,DESC`;
      } else {
        const encoded = searchValue.replace("+", "%2B");
        return `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}?searchKeyword=${encoded}&page=${page}&size=${size}&sort=createdAt,DESC`;
      }
    },
  };

  const descriptor = new ApiServiceManager(
    url.value,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const addNewOrganization = (body: any): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addNewOrganizationUser = (
  body: IAddNewUserBody
): Promise<IClientAdminResponse> => {
  const url = `${AUTH_BASE_URL}${ORGANISATIONS_ADMIN_URL}/organization-admin-sign-up`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const editOrganization = (code: string, body: any): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const deleteOrganizationAPI = (
  code: string,
  body: any
): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getOrganization = (code: string): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationPaymentMethods = (code: string): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/paymentTypes`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationBanks = (
  code: string
): Promise<IOrganizationBankInfoResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/banks`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationDocumentations = (
  code: string
): Promise<IOrganizationDocumentationResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationTiers = (
  code: string
): Promise<IOrganizationTierResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/fee-templates/active/details`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Business self-registration — creates the org as PENDING, grants the caller CLIENT_ADMIN. */
export const selfRegisterOrganization = (
  body: IOrganizationSelfRegisterBody
): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/self-register`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/**
 * Saves the organization's commercial register + unified numbers through updateOrganization
 * (PATCH /{code}). Must succeed before saveOrganizationAddressBankInfo sends a bank account,
 * which the backend validates against these numbers.
 */
export const updateOrganizationRegistryNumbers = (
  code: string,
  body: IOrganizationRegistryNumbersBody
): Promise<any> => editOrganization(code, body);

/**
 * Saves the organization's address and/or bank account (PUT /{code}/address-bank-info). Each part
 * supplied replaces the current one, so the same call adds or edits. Bank info is rejected unless
 * the organization already has a CR or unified number — the IBAN is validated against them.
 *
 * Right after self-registration the access token doesn't carry CLIENT_ADMIN yet, so callers must
 * force a token refresh first (see forceRefreshAccessToken).
 */
export const saveOrganizationAddressBankInfo = (
  code: string,
  body: IOrganizationAddressBankInfoBody
): Promise<IOrganizationAddressBankInfoResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/address-bank-info`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

/** Registers an already-uploaded bucket object as an organization attachment. */
export const addOrganizationDocumentation = (
  code: string,
  body: IAddOrganizationDocumentationBody
): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const ACCEPTED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
];

export const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Uploads an organization logo and saves it on the organization, returning the stored URL.
 *
 * Uses the public direct upload rather than the presigned flow the attachments use: `logoUrl` is
 * persisted and rendered in an <img>, so it needs a durable public URL, not a presigned one that
 * expires. This mirrors how the admin portal sets organization logos.
 */
export const uploadOrganizationLogo = async (
  code: string,
  file: File
): Promise<string> => {
  const logoUrl = await uploadFile(file, EUploadImageType.ORGANIZATION, code);
  await editOrganization(code, { logoUrl });
  return logoUrl;
};

/** A required onboarding document, paired with the file the user picked for it. */
export interface IOrganizationDocumentUpload {
  /** Machine identifier for the document slot (see REQUIRED_ORGANIZATION_DOCUMENTS). */
  type: string;
  /** Human-readable name — becomes the attachment title the admin reviewer sees. */
  title: string;
  file: File;
}

/**
 * Submits the onboarding documents for an organization, mirroring how the admin portal uploads
 * organization attachments: presigned PUT straight to the bucket, then register each object on
 * /organizations/{code}/attachments by bucket + key.
 *
 * Uploads run in parallel and the whole call rejects if any one fails, so the UI can surface a
 * single error. Note that leaves partial success possible — earlier documents may already be
 * registered when a later one fails. Re-submitting re-uploads everything, which the backend
 * treats as additional attachments rather than replacements.
 */
export const submitOrganizationDocuments = async (
  code: string,
  documents: IOrganizationDocumentUpload[]
): Promise<void> => {
  await Promise.all(
    documents.map(async ({ title, file }) => {
      const { bucket, key } = await uploadFileWithPresignedUrl(
        file,
        EUploadImageType.ORGANIZATION,
        code
      );
      return addOrganizationDocumentation(code, {
        title,
        bucketName: bucket,
        objectName: key,
        description: file.name,
      });
    })
  );
};

/**
 * Real-time, DB-backed organization state for the logged-in account (see useOrganizationState) —
 * the only reliable source for verificationStatus. organizationAdminCode/clientAdminClientId JWT
 * claims are set as soon as an account is linked to an org (not only once verified), so their
 * presence can't be used to infer approval status.
 */
export const getMyOrganization = (): Promise<any> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/my-organization`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Self-service "API clients" section (design doc §9 Phase 2) — every client for this org. */
export const getOrganizationClients = (
  code: string
): Promise<IOrgClientListResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/**
 * Creates a new API client for this org — always auto-accept-consent=false regardless of the
 * org's other clients (a staff-only, compliance-reviewed exception, see admin-app). The response's
 * clientSecret is shown once and never retrievable again.
 */
export const createOrganizationClient = (
  code: string,
  body: { description?: string }
): Promise<IOrgClientCreatedResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/clients`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};
